import { useEffect, useState, useCallback } from 'react';
import api from './api';

/**
 * Hook + module-cache for Signature Features feature flags.
 * Mirrors the useMenu pattern so multiple components share one network request.
 *
 * Returns { features, loading, isOn, refetch }
 *  - features: full config object keyed by feature slug
 *  - isOn(slug): boolean shortcut
 */
let _cache = null;
let _inflight = null;
const _subscribers = new Set();

const DEFAULT_OFF = {
  cinematic_read: { enabled: false },
  forge: { enabled: false },
  oracle: { enabled: false },
  pirate_radio: { enabled: false },
  proof_of_life: { enabled: false },
};

const notify = () => { for (const fn of _subscribers) fn(_cache); };

export const fetchFeatures = async (force = false) => {
  if (!force && _cache) return _cache;
  if (!force && _inflight) return _inflight;
  _inflight = api
    .get('/features')
    .then((r) => {
      _cache = { ...DEFAULT_OFF, ...(r.data || {}) };
      notify();
      return _cache;
    })
    .catch(() => {
      _cache = { ...DEFAULT_OFF };
      notify();
      return _cache;
    })
    .finally(() => { _inflight = null; });
  return _inflight;
};

export const invalidateFeaturesCache = () => { _cache = null; fetchFeatures(true); };

export function useFeatures() {
  const [data, setData] = useState(_cache || DEFAULT_OFF);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    let mounted = true;
    const sub = (val) => { if (mounted) setData(val || DEFAULT_OFF); };
    _subscribers.add(sub);
    if (!_cache) {
      fetchFeatures().then((val) => { if (mounted) { setData(val); setLoading(false); } });
    } else {
      setLoading(false);
    }
    return () => { mounted = false; _subscribers.delete(sub); };
  }, []);

  const refetch = useCallback(() => fetchFeatures(true), []);
  const isOn = useCallback((slug) => Boolean(data?.[slug]?.enabled), [data]);

  return { features: data, loading, refetch, isOn };
}
